import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Button from 'tio-reimagined/components/button';
import Modal from 'tio-reimagined/components/modal';
import { VStack } from 'tio-reimagined/components/layout';
import { PhWarning } from 'ember-phosphor-icons';
import { updateReportRequest } from '../requests/report';
import { SkipCache } from '@warp-drive/core-types/request';
import type StoreService from 'ember-data/store';
import type Report from 'tio-data/models/reports';
import type Owner from '@ember/owner';

export type PublishReportAction = 'publish' | 'unpublish';

interface PublishReportDialogSignature {
  Args: {
    isOpen: boolean;
    onClose: () => void;
    onPublish: (report: Report) => void;
    report: Report;
    action?: PublishReportAction;
    currentCompanyId?: string | null;
    companyName?: string;
  };
}

export default class PublishReportDialog extends Component<PublishReportDialogSignature> {
  @service declare store: StoreService;

  @tracked isPublishing = false;
  @tracked hasAcknowledgedRisk = false;

  constructor(owner: Owner, args: PublishReportDialogSignature['Args']) {
    super(owner, args);
  }

  get companyId() {
    return this.args.currentCompanyId || this.args.report?.metadata?.publishedCompanyId || null;
  }

  get visibilityLevel() {
    return this.companyId ? 'partner' : 'global';
  }

  get partnerAccessConfig() {
    return this.companyId ? [Number(this.companyId)] : [];
  }

  get action() {
    return this.args.action || 'publish';
  }

  get isUnpublish() {
    return this.action === 'unpublish';
  }

  get actionLabel() {
    return this.isUnpublish ? 'Unpublish' : 'Publish';
  }

  get runningLabel() {
    return this.isUnpublish ? 'Unpublishing...' : 'Publishing...';
  }

  get headerText() {
    return this.isUnpublish ? 'Unpublish Report?' : 'Publish Report?';
  }

  get acknowledgementText() {
    return this.isUnpublish
      ? 'I understand this is a sensitive action and have verified the report should be unpublished.'
      : 'I understand this is a sensitive action and have verified the report is safe to publish.';
  }

  handleConfirm = async () => {
    const report = this.args.report;
    if (!report?.id || !this.hasAcknowledgedRisk) return;

    this.isPublishing = true;
    try {
      const metadata = { ...(report.metadata || {}) };
      let request;

      if (this.isUnpublish) {
        metadata.scope = 'admin_only';
        delete metadata.publishedAt;
        delete metadata.publishedCompanyId;

        request = updateReportRequest(report.id, {
          visibilityLevel: report.visibilityLevel || 'private',
          partnerAccessConfig: report.partnerAccessConfig || [],
          metadata,
        });
      } else {
        metadata.scope = 'partner_admin';
        metadata.publishedAt = new Date().toISOString();
        metadata.publishedCompanyId = this.companyId || undefined;

        request = updateReportRequest(report.id, {
          visibilityLevel: this.visibilityLevel,
          partnerAccessConfig: this.partnerAccessConfig,
          metadata,
        });
      }

      request.cacheOptions = { [SkipCache]: true };
      const response = await this.store.request(request);
      const attrs = (response.content as { data: { attributes: Record<string, unknown> } })?.data
        ?.attributes;
      if (attrs) {
        Object.assign(report, attrs);
      }
      this.args.onPublish(report);
      this.args.onClose();
      this.hasAcknowledgedRisk = false;
    } catch (error) {
      console.error(`Failed to ${this.action} report:`, error);
    } finally {
      this.isPublishing = false;
    }
  };

  handleCancel = () => {
    this.hasAcknowledgedRisk = false;
    this.args.onClose();
  };

  handleAcknowledgementChange = (event: Event) => {
    this.hasAcknowledgedRisk = (event.target as HTMLInputElement).checked;
  };

  <template>
    <Modal @isOpen={{@isOpen}} @onClose={{this.handleCancel}} @size="md" data-test-publish-dialog>
      <:header>
        <div class="flex items-center gap-3 text-orange-700 dark:text-orange-300">
          <span
            class="flex h-9 w-9 items-center justify-center rounded-full bg-orange-100 text-orange-600 dark:bg-orange-900/40 dark:text-orange-300"
          >
            <PhWarning @size={{20}} />
          </span>
          <span class="text-base font-semibold">{{this.headerText}}</span>
        </div>
      </:header>

      <:body>
        <VStack class="flex flex-col gap-y-4">
          <div class="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <p>
              {{#if this.isUnpublish}}
                You are about to remove partner admin access to this report. Users who rely on this
                report may lose access immediately.
              {{else}}
                You are about to publish this report to partner admins. Published reports can expose
                configured report data outside the admin-only workspace.
              {{/if}}
            </p>
            <p class="font-semibold text-gray-900 dark:text-gray-100">
              {{#if this.isUnpublish}}
                Only unpublish after verifying this report should no longer be available to partner
                admins.
              {{else}}
                Only publish after verifying the report columns, filters, visibility, and company
                access are correct.
              {{/if}}
            </p>
          </div>

          <div
            class="rounded-md border border-orange-200 bg-orange-50 p-3 text-sm dark:border-orange-800 dark:bg-orange-950/30"
          >
            <p class="font-semibold text-orange-800 dark:text-orange-300">
              {{if this.isUnpublish "Unpublishing risk" "Publishing risk"}}
            </p>
            <ul class="mt-2 list-inside list-disc space-y-1 text-orange-700 dark:text-orange-300">
              {{#if this.isUnpublish}}
                <li>Partner admins may immediately lose access to this report.</li>
                <li>Dashboards, documentation, or workflows that reference the report may break.</li>
                <li>Users may lose visibility into expected operational reporting.</li>
              {{else}}
                <li>Partner admins may immediately access this report.</li>
                <li>Incorrect visibility can expose data to the wrong audience.</li>
                <li>Incorrect filters or columns can publish sensitive or misleading report results.</li>
              {{/if}}
            </ul>
          </div>

          <div
            class="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-800"
          >
            <p class="font-medium text-gray-700 dark:text-gray-300">What will happen</p>
            <ul class="mt-1 list-inside list-disc text-gray-600 dark:text-gray-400">
              <li>
                <span class="font-medium">Scope:</span>
                {{#if this.isUnpublish}}
                  Admin Only — this report will no longer be visible to partner admins
                {{else}}
                  Partner Admin — this report will be visible outside of admin
                {{/if}}
              </li>
              {{#unless this.isUnpublish}}
                <li>
                  <span class="font-medium">Visibility:</span>
                  {{#if this.companyId}}
                    TIO Admin Public — only
                    <strong class="text-gray-800 dark:text-gray-200">{{if
                        @companyName
                        @companyName
                        "the selected company"
                      }}</strong>
                    can see this report
                  {{else}}
                    Global — all partner admins can see this report
                  {{/if}}
                </li>
              {{/unless}}
            </ul>
          </div>

          <label
            class="flex items-start gap-2 rounded-md border border-gray-200 bg-white p-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300"
          >
            <input
              type="checkbox"
              checked={{this.hasAcknowledgedRisk}}
              class="mt-0.5 h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              {{on "change" this.handleAcknowledgementChange}}
            />
            <span>
              {{this.acknowledgementText}}
            </span>
          </label>
        </VStack>
      </:body>

      <:footer>
        <div class="flex justify-end gap-x-3">
          <Button @appearance="outlined" {{on "click" this.handleCancel}} data-test-publish-cancel>
            Cancel
          </Button>
          <Button
            @intent="danger"
            @isRunning={{this.isPublishing}}
            @isDisabled={{if this.hasAcknowledgedRisk false true}}
            {{on "click" this.handleConfirm}}
            data-test-publish-submit
          >
            {{if this.isPublishing this.runningLabel this.actionLabel}}
          </Button>
        </div>
      </:footer>
    </Modal>
  </template>
}
